import { useState } from 'react';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

export default function StructureChart({ calc, inputs }) {
  const [editing, setEditing] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [customEntities, setCustomEntities] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntity, setNewEntity] = useState({ label: '', description: '', amount: '' });
  const [editDraft, setEditDraft] = useState({ label: '', description: '', amount: '' });

  const subAllocPct = inputs?.subAllocationPct ?? 4;

  const defaults = {
    'leverage-lender': {
      label: 'Leverage Lender',
      description: 'A bank or financial institution providing the senior loan to the Investment Fund. They lend at market rates alongside the equity investment.',
    },
    'tc-investor': {
      label: 'Tax Credit Investor',
      description: `A corporation or bank investing equity to claim ${fmt(calc.totalTaxCredits)} in federal tax credits over 7 years (39% of QEI).`,
    },
    'investment-fund': {
      label: 'Investment Fund LLC',
      description: 'A special-purpose LLC that receives both the leverage loan and equity, then makes the Qualified Equity Investment (QEI) into the CDE.',
    },
    cde: {
      label: 'Sub-CDE (Loan Originator)',
      description: 'The subsidiary CDE that receives the QEI and originates QLICI loans (A-Note and B-Note) to the borrower. Pays a sub-allocation fee to the parent CDE for use of the NMTC allocation.',
    },
    'cde-parent': {
      label: 'CDE Parent / Allocatee',
      description: `The primary allocatee that holds the NMTC allocation from the CDFI Fund. Receives a ${subAllocPct.toFixed(1)}% sub-allocation fee (${fmt(calc.subAllocationFee)}) from the Sub-CDE at closing.`,
    },
    borrower: {
      label: 'Borrower / QALICB',
      description: 'Your organization — a Qualified Active Low-Income Community Business. Receives the A-Note and B-Note from the CDE.',
    },
  };

  const getLabel = (id) => overrides[id]?.label ?? defaults[id]?.label ?? '';
  const getDesc = (id) => overrides[id]?.description ?? defaults[id]?.description ?? '';

  const startEdit = (id, currentLabel, currentDesc, amount = '') => {
    setEditing(id);
    setEditDraft({ label: currentLabel, description: currentDesc, amount });
  };

  const saveEdit = (id, isCustom = false) => {
    if (isCustom) {
      setCustomEntities((prev) =>
        prev.map((e) =>
          e.id === id
            ? { ...e, label: editDraft.label, description: editDraft.description, amount: editDraft.amount }
            : e
        )
      );
    } else {
      setOverrides((prev) => ({
        ...prev,
        [id]: { label: editDraft.label, description: editDraft.description },
      }));
    }
    setEditing(null);
  };

  const cancelEdit = () => setEditing(null);

  const deleteCustom = (id) => {
    setCustomEntities((prev) => prev.filter((e) => e.id !== id));
  };

  const addEntity = () => {
    if (!newEntity.label.trim()) return;
    setCustomEntities((prev) => [
      ...prev,
      {
        id: `custom-${Date.now()}`,
        label: newEntity.label.trim(),
        description: newEntity.description.trim(),
        amount: newEntity.amount.trim(),
      },
    ]);
    setNewEntity({ label: '', description: '', amount: '' });
    setShowAddForm(false);
  };

  const EditButtons = ({ id, isCustom = false }) => (
    <div className="flex gap-2 mt-3">
      <button
        onClick={() => saveEdit(id, isCustom)}
        className="px-3 py-1 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
      >
        Save
      </button>
      <button
        onClick={cancelEdit}
        className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
      >
        Cancel
      </button>
    </div>
  );

  const EntityEditForm = ({ id, showAmount = false }) => (
    <div className="space-y-2">
      <input
        type="text"
        value={editDraft.label}
        onChange={(e) => setEditDraft((d) => ({ ...d, label: e.target.value }))}
        className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-400"
        placeholder="Entity name"
      />
      <textarea
        value={editDraft.description}
        onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))}
        className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1 text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
        rows={3}
        placeholder="Description"
      />
      {showAmount && (
        <input
          type="text"
          value={editDraft.amount}
          onChange={(e) => setEditDraft((d) => ({ ...d, amount: e.target.value }))}
          className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400"
          placeholder="Amount (optional)"
        />
      )}
      {!showAmount && (
        <p className="text-xs text-gray-400 italic">Amounts auto-update from your inputs</p>
      )}
      <EditButtons id={id} isCustom={showAmount} />
    </div>
  );

  const EntityCard = ({ id, colorClasses, amount, sublabel }) => {
    const isEditing = editing === id;
    const label = getLabel(id);
    const desc = getDesc(id);

    return (
      <div className={`rounded-xl border p-4 ${colorClasses}`}>
        {isEditing ? (
          <EntityEditForm id={id} showAmount={false} />
        ) : (
          <>
            <div className="flex items-start justify-between gap-2">
              <span className="font-bold text-sm text-gray-800 leading-snug">{label}</span>
              <button
                onClick={() => startEdit(id, label, desc)}
                className="text-base opacity-40 hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
                title="Edit entity"
              >
                ✏️
              </button>
            </div>
            <div className="font-mono font-bold text-lg text-gray-900 mt-1">{amount}</div>
            {sublabel && <div className="text-xs text-gray-500 mt-0.5">{sublabel}</div>}
            <p className="text-sm text-gray-600 mt-1 leading-snug">{desc}</p>
          </>
        )}
      </div>
    );
  };

  const CustomEntityCard = ({ entity }) => {
    const isEditing = editing === entity.id;
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 relative w-56">
        {isEditing ? (
          <EntityEditForm id={entity.id} showAmount={true} />
        ) : (
          <>
            <div className="flex items-start justify-between gap-2">
              <span className="font-bold text-sm text-gray-700 leading-snug">{entity.label}</span>
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() => startEdit(entity.id, entity.label, entity.description, entity.amount)}
                  className="text-sm opacity-40 hover:opacity-100 transition-opacity"
                  title="Edit"
                >
                  ✏️
                </button>
                <button
                  onClick={() => deleteCustom(entity.id)}
                  className="text-sm opacity-40 hover:opacity-100 transition-opacity text-red-500"
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            </div>
            {entity.amount && (
              <div className="font-mono font-bold text-lg text-gray-900 mt-1">{entity.amount}</div>
            )}
            {entity.description && (
              <p className="text-sm text-gray-600 mt-1 leading-snug">{entity.description}</p>
            )}
          </>
        )}
      </div>
    );
  };

  const ArrowDown = ({ color = '#64748b', size = 10 }) => (
    <svg width={size * 2} height={size} viewBox={`0 0 ${size * 2} ${size}`} className="block mx-auto">
      <polygon points={`0,0 ${size * 2},0 ${size},${size}`} fill={color} />
    </svg>
  );

  const DownConnector = ({ label }) => (
    <div className="flex flex-col items-center py-1">
      <div className="w-0.5 bg-slate-400 h-5" />
      <span className="text-xs font-medium text-slate-600 text-center px-1 my-0.5">{label}</span>
      <ArrowDown color="#64748b" size={8} />
    </div>
  );

  return (
    <div className="space-y-2 max-w-3xl mx-auto font-sans">
      {/* ── Top: Leverage Lender + Tax Credit Investor ── */}
      <div className="grid grid-cols-2 gap-4">
        <EntityCard
          id="leverage-lender"
          colorClasses="bg-blue-50 border-blue-300"
          amount={fmt(calc.leverageLoan)}
          sublabel={`${Math.round(calc.leveragePct)}% of QEI`}
        />
        <EntityCard
          id="tc-investor"
          colorClasses="bg-purple-50 border-purple-300"
          amount={fmt(calc.nmtcEquity)}
          sublabel={`${Math.round(calc.equityPct)}% of QEI`}
        />
      </div>

      {/* ── Merge connectors ── */}
      <div className="relative flex items-start justify-center">
        <div className="flex flex-col items-center flex-1">
          <div className="w-0.5 bg-slate-400 h-5 mx-auto" />
          <span className="text-xs font-medium text-slate-600">Leverage Loan</span>
        </div>
        <div className="flex flex-col items-center flex-1">
          <div className="w-0.5 bg-slate-400 h-5 mx-auto" />
          <span className="text-xs font-medium text-slate-600">Equity</span>
        </div>
      </div>
      <div className="relative flex flex-col items-center">
        <div className="w-1/2 h-0.5 bg-slate-400" />
        <div className="w-0.5 bg-slate-400 h-4" />
        <ArrowDown color="#64748b" size={8} />
      </div>

      {/* ── Investment Fund ── */}
      <div className="px-16">
        <EntityCard
          id="investment-fund"
          colorClasses="bg-emerald-50 border-emerald-400"
          amount={fmt(calc.qei)}
          sublabel="Total QEI deployed"
        />
      </div>

      <DownConnector label="QEI (Qualified Equity Investment)" />

      {/* ── Sub-CDE + CDE Parent (side by side with fee arrow) ── */}
      <div className="relative">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-0">
          {/* Sub-CDE box */}
          <div className="pl-2 pr-1">
            <EntityCard
              id="cde"
              colorClasses="bg-emerald-50 border-emerald-400"
              amount={fmt(calc.qei)}
              sublabel="NMTC Allocation"
            />
          </div>

          {/* Horizontal fee arrow */}
          <div className="flex flex-col items-center px-2 min-w-[110px]">
            <span className="text-xs font-bold text-red-600 whitespace-nowrap mb-1">
              {fmt(calc.subAllocationFee)}
            </span>
            <div className="flex items-center w-full">
              <div className="flex-1 border-t-2 border-dashed border-red-400" />
              <svg width="12" height="10" viewBox="0 0 12 10" className="block flex-shrink-0 -ml-px">
                <polygon points="0,0 12,5 0,10" fill="#f87171" />
              </svg>
            </div>
            <span className="text-xs font-medium text-red-500 whitespace-nowrap mt-1">
              Sub-Alloc Fee ({subAllocPct.toFixed(1)}%)
            </span>
          </div>

          {/* CDE Parent box */}
          <div className="pl-1 pr-2">
            <EntityCard
              id="cde-parent"
              colorClasses="bg-orange-50 border-orange-300"
              amount={fmt(calc.subAllocationFee)}
              sublabel={`${subAllocPct.toFixed(1)}% of QEI at closing`}
            />
          </div>
        </div>
      </div>

      {/* ── A-Note / B-Note split from Sub-CDE ── */}
      <div className="flex items-start pl-2 pr-1 mt-1">
        <div className="flex-1 flex flex-col items-center" style={{ maxWidth: 'calc(66.67% - 50px)' }}>
          <div className="flex w-full">
            <div className="flex-1 flex flex-col items-center">
              <div className="w-0.5 bg-slate-400 h-5 mx-auto" />
              <span className="text-xs text-slate-700 font-semibold text-center">A-Note {fmt(calc.aNote)}</span>
              <span className="text-xs text-slate-400 text-center">{(calc.aNoteRate * 100).toFixed(2)}% interest</span>
              <ArrowDown color="#64748b" size={7} />
            </div>
            <div className="flex-1 flex flex-col items-center">
              <div className="w-0.5 bg-slate-400 h-5 mx-auto" />
              <span className="text-xs text-slate-600 font-semibold text-center">B-Note {fmt(calc.bNote)}</span>
              <span className="text-xs text-slate-400 text-center">{(calc.bNoteRate * 100).toFixed(2)}% — forgivable</span>
              <ArrowDown color="#64748b" size={7} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Borrower / QALICB ── */}
      <div className="px-4">
        <EntityCard
          id="borrower"
          colorClasses="bg-amber-50 border-amber-400"
          amount={fmt(calc.netProceedsToProject)}
          sublabel="Net proceeds to project"
        />
      </div>

      {/* ── Custom entities ── */}
      {customEntities.length > 0 && (
        <div className="flex flex-wrap gap-3 pt-2">
          {customEntities.map((entity) => (
            <CustomEntityCard key={entity.id} entity={entity} />
          ))}
        </div>
      )}

      {/* ── Add entity button ── */}
      <div className="pt-2">
        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full border-2 border-dashed border-gray-300 rounded-xl py-3 text-sm text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors"
          >
            + Add Entity to This Structure
          </button>
        ) : (
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3">
            <p className="text-xs text-gray-500">
              Add entities specific to your deal — Project Sponsor LLC, co-lender, etc.
            </p>
            <input
              type="text"
              value={newEntity.label}
              onChange={(e) => setNewEntity((n) => ({ ...n, label: e.target.value }))}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Entity name (required)"
            />
            <textarea
              value={newEntity.description}
              onChange={(e) => setNewEntity((n) => ({ ...n, description: e.target.value }))}
              className="w-full text-xs border border-gray-300 rounded-lg px-3 py-1.5 text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
              rows={2}
              placeholder="Description (optional)"
            />
            <input
              type="text"
              value={newEntity.amount}
              onChange={(e) => setNewEntity((n) => ({ ...n, amount: e.target.value }))}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Amount (optional, e.g. $2,500,000)"
            />
            <div className="flex gap-2">
              <button
                onClick={addEntity}
                disabled={!newEntity.label.trim()}
                className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Add to Chart
              </button>
              <button
                onClick={() => { setShowAddForm(false); setNewEntity({ label: '', description: '', amount: '' }); }}
                className="px-4 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Year 7 callout ── */}
      <div className="mt-4 border-l-4 border-amber-400 bg-amber-50 rounded-r-xl px-4 py-3">
        <p className="text-sm text-amber-900 leading-relaxed">
          <span className="font-semibold">Year 7 Unwind:</span> Tax Credit Investor sells
          Investment Fund interest to Borrower at nominal value (~$1). B-Note forgiven. Compliance
          period ends. Borrower may refinance A-Note at market rates.
        </p>
      </div>
    </div>
  );
}
